'use client';

/**
 * useComponentPortals Hook
 *
 * Manages portal state and component rendering for the DynamicCriteriaGenerator.
 * Uses the component registry for streamlined rendering.
 */

import { useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { createComponentElement } from './componentRegistry';
import {
  fromReduxInput,
  ComponentData,
} from '../utils/criteriaTransformer';

interface PortalData {
  id: string;
  target: HTMLElement;
  component: ReactNode;
}

interface PendingPlaceholder {
  placeholder: HTMLElement;
  confirmed: boolean;
}

interface UseComponentPortalsParams {
  updateComponentInput: (componentId: string, data: any) => void;
  removeComponentInput: (componentId: string) => void;
  updateCriteria: () => void;
  editorRef: React.RefObject<HTMLDivElement>;
  pendingPlaceholdersRef?: React.MutableRefObject<Map<string, PendingPlaceholder>>;
}

interface UseComponentPortalsReturn {
  portals: Map<string, PortalData>;
  pendingPortalUpdateRef: React.MutableRefObject<{
    componentId: string;
    updatedData: any;
    target: HTMLElement | undefined;
  } | null>;
  renderComponentByType: (
    pillElement: HTMLElement,
    componentData: ComponentData,
    inputData: any
  ) => void;
  handleComponentUpdate: (componentId: string, data: any) => void;
  handleRemoveComponent: (componentId: string) => void;
  createComponentData: (componentId: string, input: any) => ComponentData;
  setPendingPortalUpdate: (update: { componentId: string; updatedData: any; target: HTMLElement | undefined } | null) => void;
}

export function useComponentPortals({
  updateComponentInput,
  removeComponentInput,
  updateCriteria,
  editorRef,
  pendingPlaceholdersRef,
}: UseComponentPortalsParams): UseComponentPortalsReturn {
  const [portals, setPortals] = useState<Map<string, PortalData>>(new Map());
  const pendingPortalUpdateRef = useRef<{
    componentId: string;
    updatedData: any;
    target: HTMLElement | undefined;
  } | null>(null);

  /**
   * Creates ComponentData from Redux input format using centralized transformer.
   */
  const createComponentData = useCallback(
    (componentId: string, input: any): ComponentData => fromReduxInput(componentId, input),
    []
  );

  /**
   * Handles component data updates from child components.
   * When data is received for a component created from a placeholder,
   * confirms the placeholder (removes it permanently).
   */
  const handleComponentUpdate = useCallback(
    (componentId: string, data: any) => {
      updateComponentInput(componentId, data);

      // If this component came from a placeholder and has data, confirm it
      if (pendingPlaceholdersRef?.current) {
        const pending = pendingPlaceholdersRef.current.get(componentId);
        if (pending && !pending.confirmed) {
          // Mark as confirmed and remove the hidden placeholder permanently
          pending.confirmed = true;
          pending.placeholder.remove();
          pendingPlaceholdersRef.current.delete(componentId);
        }
      }

      setTimeout(() => {
        updateCriteria();
      }, 0);
    },
    [updateComponentInput, updateCriteria, pendingPlaceholdersRef]
  );

  /**
   * Handles component removal.
   * If the component came from a placeholder that wasn't confirmed,
   * restores the placeholder instead of just removing.
   */
  const handleRemoveComponent = useCallback(
    (componentId: string) => {
      // Check if this component came from a placeholder that wasn't confirmed
      const pending = pendingPlaceholdersRef?.current?.get(componentId);

      // Remove pill from DOM
      const pillElement = editorRef.current?.querySelector(
        `[data-component-id="${componentId}"]`
      );

      if (pending && !pending.confirmed) {
        // Restore the placeholder
        pending.placeholder.style.display = '';
        pendingPlaceholdersRef?.current?.delete(componentId);
      }

      if (pillElement) {
        pillElement.remove();
      }

      // Remove portal
      setPortals((prev) => {
        const newPortals = new Map(prev);
        newPortals.delete(componentId);
        return newPortals;
      });

      removeComponentInput(componentId);
      updateCriteria();
    },
    [editorRef, removeComponentInput, updateCriteria, pendingPlaceholdersRef]
  );

  /**
   * Sets pending portal update for slash command merge.
   */
  const setPendingPortalUpdate = useCallback(
    (update: { componentId: string; updatedData: any; target: HTMLElement | undefined } | null) => {
      pendingPortalUpdateRef.current = update;
    },
    []
  );

  /**
   * Renders a component into a portal based on its type.
   * Uses the component registry for streamlined rendering.
   */
  const renderComponentByType = useCallback(
    (pillElement: HTMLElement, componentData: ComponentData, inputData: any) => {
      const component = createComponentElement({
        componentId: componentData.id,
        type: componentData.type,
        inputData,
        onUpdate: handleComponentUpdate,
        onRemove: handleRemoveComponent,
        updateComponentInput,
      });

      // Add portal to state
      if (component) {
        setPortals((prev) => {
          const newPortals = new Map(prev);
          newPortals.set(componentData.id, {
            id: componentData.id,
            target: pillElement,
            component,
          });
          return newPortals;
        });
      }
    },
    [handleComponentUpdate, handleRemoveComponent, updateComponentInput]
  );

  // Process pending portal updates (from slash command merge)
  useEffect(() => {
    const pending = pendingPortalUpdateRef.current;
    if (pending && pending.target) {
      const { componentId, updatedData, target } = pending;
      const componentData = createComponentData(componentId, updatedData);
      renderComponentByType(target, componentData, updatedData);
      pendingPortalUpdateRef.current = null;
    }
  });

  return {
    portals,
    pendingPortalUpdateRef,
    renderComponentByType,
    handleComponentUpdate,
    handleRemoveComponent,
    createComponentData,
    setPendingPortalUpdate,
  };
}
